import type { PerformanceEventProperties } from '@ms/yammer-telemetry';
import type { PageLoadPerformanceEventProperties } from '@ms/yammer-telemetry-support';
import type { Emitter } from 'mitt';
import mitt from 'mitt';

export type PageLoadEventName = 'Loaded' | 'TimedOut' | 'Failed' | 'FailedRecoverable' | 'Interrupted';
export type RouteChangedEventName = 'RouteChanged';

/**
 * Payload for a page load lifecycle event. `pageLoadId` identifies the specific page load that
 * produced the event so subscribers can correlate a terminal outcome with the exact load it
 * belongs to and ignore events emitted by a superseded load of the same page.
 */
export type PageLoadEventData = PerformanceEventProperties<PageLoadPerformanceEventProperties> & {
  readonly pageLoadId?: number;
};

type LoadEvents = Record<PageLoadEventName, PageLoadEventData>;
type RouteChangedEvents = Record<RouteChangedEventName, void>;
type PageLoadEvents = LoadEvents & RouteChangedEvents;

let emitter: Emitter<PageLoadEvents>;

type SubscribeToPageLoadEvent = <E extends keyof PageLoadEvents>(
  event: E,
  callback: (data: PageLoadEvents[E]) => void
) => void;
export const subscribeToPageLoadEvent: SubscribeToPageLoadEvent = (event, callback) => {
  if (!emitter) {
    emitter = mitt<PageLoadEvents>();
  }
  emitter.on(event, callback);
};

type UnsubscribeFromPageLoadEvent = <E extends keyof PageLoadEvents>(
  event: E,
  callback: (data: PageLoadEvents[E]) => void
) => void;
export const unsubscribeFromPageLoadEvent: UnsubscribeFromPageLoadEvent = (event, callback) => {
  if (!emitter) {
    return;
  }

  emitter.off(event, callback);
};

/**
 * @internal
 * Emits a page load lifecycle event. This should be considered a private API and should
 * not be called outside of the page-route-telemetry package except for test scenarios.
 */
export function emitPageLoadEvent<E extends PageLoadEventName>(event: E, data: PageLoadEventData): void;
export function emitPageLoadEvent<E extends RouteChangedEventName>(event: E): void;
export function emitPageLoadEvent(event: PageLoadEventName | RouteChangedEventName, data?: PageLoadEventData): void {
  if (!emitter) {
    return;
  }

  emitter.emit(event, data);
}
